import React, { Suspense, lazy, useEffect, useSyncExternalStore } from "react";
import ReactDOM from "react-dom/client";

import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";

import { ThemeProvider } from "styled-components";
import theme from "~/constants/theme";

import GlobalStyle from "~/globalStyle";
import { ToastContainer } from "~/components/toast";
import { maintenance, startRecovery } from "~/helpers/maintenance";
import { MaintenancePage } from "~/components/maintenance";

import { routes } from "~/router/routes";

import "~/translations/i18n";

import { analyticsSetup } from "./helpers/analytics";
import { initDatadogRum } from "./helpers/datadog";
import { intercomKey } from "./helpers/environment";
import { ContextWrapper } from "./contexts";

import SuspenseFallback from "~/components/layout/SuspenseFallback";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import { GoogleTagManager } from "./components/google-tag-manager";
import { reportWebVitals } from "./helpers/webVitals";
import { initStatsig } from "./helpers/statsig";
import { captureClickIdsFromSearch } from "~/helpers/clickIds";

captureClickIdsFromSearch();

// Font loaded asynchronously to avoid blocking FCP (render-blocking CSS removed)
import("@fontsource-variable/inter/slnt.css").catch(() => {});

const ReactQueryDevtools = lazy(() =>
  import("@tanstack/react-query-devtools").then((m) => ({
    default: m.ReactQueryDevtools,
  }))
);

if (typeof window !== "undefined") {
  // After a deploy, a user still on the old index.html requests hashed chunks
  // that no longer exist, so a lazy import fails ("Failed to fetch dynamically
  // imported module" / "Importing a module script failed" / "Unable to preload
  // CSS"). Vite dispatches `vite:preloadError` — reload once to pick up the new
  // build, guarded so we never loop.
  window.addEventListener("vite:preloadError", () => {
    const KEY = "vite_preload_reloaded";
    try {
      // Reload at most once per session — if the chunk still fails after the
      // reload (genuinely broken deploy / bad cache), don't loop.
      if (window.sessionStorage.getItem(KEY)) return;
      window.sessionStorage.setItem(KEY, "1");
    } catch {
      // sessionStorage can be unavailable (privacy mode / blocked storage);
      // skip the auto-reload rather than risk crashing startup or looping.
      return;
    }
    window.location.reload();
  });
}

if (typeof window !== "undefined") {
  void initStatsig();

  const runNonCritical = () => {
    analyticsSetup();
    initDatadogRum();
    reportWebVitals();
  };

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(runNonCritical);
  } else {
    setTimeout(runNonCritical, 0);
  }
}

const router = createBrowserRouter(routes);

const isProduction = import.meta.env.PROD;

/**
 * Maintenance-page outage detection (helpers/maintenance.ts, ported from
 * alidrop-app). Raw fetch, NOT this app's sendGet/axios wrapper: a 401
 * through that wrapper calls forceSignOut() and would log out every parked
 * tab. health_check is decorated AllowAny on this API (see
 * plans/maintenance-pages-all-apps/src/INTEGRATION.md), so a plain 200 here
 * is the expected "server is up" signal.
 */
const apiURL: string = import.meta.env.VITE_API_URL;

const confirmReadiness = async (): Promise<boolean> => {
  try {
    const res = await fetch(`${apiURL}/health_check/`, {
      cache: "no-store",
      signal: AbortSignal.timeout(5000),
    });
    return res.ok;
  } catch {
    return false;
  }
};

/**
 * Mounted inside ThemeProvider (so it gets brand tokens) but wrapping the
 * router (so it can replace routed content without unmounting providers).
 *
 * It DOES sit inside ContextWrapper (GoogleOAuthProvider > AccountProvider >
 * StoreProvider > ReactQueryProvider), same as every sibling app. That is
 * safe today because those providers are plain useState wrappers that fetch
 * nothing — but it does mean the gate is downstream of them, so anything
 * added there that can throw or suspend during an outage would stop this page
 * rendering. Keep them inert.
 *
 * The app stays mounted and hidden rather than swapped out: replacing
 * `children` would unmount the router subtree and destroy whatever the user
 * was in the middle of (a half-filled form, an open modal, scroll position).
 * Hiding it preserves all of that, so recovery drops them back exactly where
 * they were.
 */
function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const isDown = useSyncExternalStore(
    maintenance.subscribe,
    maintenance.getSnapshot
  );

  useEffect(() => {
    if (!isDown) return;
    return startRecovery(confirmReadiness);
  }, [isDown]);

  return (
    <>
      <div hidden={isDown}>{children}</div>
      {isDown && <MaintenancePage />}
    </>
  );
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <Provider store={store}>
      <IntercomProvider
        appId={intercomKey}
        autoBoot={false}
        apiBase={
          intercomKey
            ? `https://${intercomKey}.intercom-messenger.com`
            : undefined
        }>
        <ContextWrapper>
          <ThemeProvider theme={theme}>
            {isProduction ? <GoogleTagManager /> : null}
            <GlobalStyle />
            <MaintenanceGate>
              <Suspense fallback={<SuspenseFallback />}>
                <RouterProvider router={router} />
              </Suspense>
            </MaintenanceGate>
            <ToastContainer />
            {import.meta.env.DEV && (
              <Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} />
              </Suspense>
            )}
          </ThemeProvider>
        </ContextWrapper>
      </IntercomProvider>
    </Provider>
  </React.StrictMode>
);
